import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, CheckCircle2, Clock, StickyNote, X, Plus,
  Sparkles, BookOpen, Trash2, ChevronRight,
  Volume2, VolumeX, Pause, Play, Square
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function LessonReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [ideas, setIdeas] = useState(null);
  const [showIdeas, setShowIdeas] = useState(false);
  const [showNotePanel, setShowNotePanel] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [readProgress, setReadProgress] = useState(0);
  const contentRef = useRef(null);

  // TTS state (Web Speech API)
  const [ttsActive, setTtsActive] = useState(false);
  const [ttsPaused, setTtsPaused] = useState(false);
  const [ttsSpeed, setTtsSpeed] = useState(1);
  const utteranceRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/lessons/${id}`),
      axios.get(`${API}/notes/lesson/${id}`)
    ]).then(([lessonRes, notesRes]) => {
      setLesson(lessonRes.data);
      setNotes(notesRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  // Stop TTS on unmount
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement;
      const scrollTop = el.scrollTop || document.body.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      setReadProgress(scrollHeight > 0 ? Math.min((scrollTop / scrollHeight) * 100, 100) : 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const buildLessonText = useCallback(() => {
    if (!lesson) return "";
    const parts = [];
    parts.push(lesson.title + ".");
    if (lesson.intro) parts.push(lesson.intro);
    (lesson.sections || []).forEach(s => {
      if (s.type === "section_title") parts.push(s.text + ":");
      else if (s.text) parts.push(s.text);
    });
    if (lesson.key_takeaways?.length) {
      parts.push("Poin penting dari lesson ini:");
      lesson.key_takeaways.forEach((t, i) => parts.push(`${i + 1}. ${t}`));
    }
    return parts.join(" ");
  }, [lesson]);

  const handleTTS = () => {
    if (!window.speechSynthesis) {
      alert("Browser kamu tidak mendukung Text-to-Speech.");
      return;
    }

    // If paused, resume
    if (ttsPaused) {
      window.speechSynthesis.resume();
      setTtsPaused(false);
      return;
    }

    // If playing, pause
    if (ttsActive && !ttsPaused) {
      window.speechSynthesis.pause();
      setTtsPaused(true);
      return;
    }

    // Start new TTS
    window.speechSynthesis.cancel();
    const text = buildLessonText();

    const startSpeak = () => {
      const utterance = new SpeechSynthesisUtterance(text);
      // Try id-ID voice first, fall back to any available
      const voices = window.speechSynthesis.getVoices();
      const idVoice = voices.find(v => v.lang.startsWith("id")) ||
                      voices.find(v => v.lang.startsWith("en")) ||
                      voices[0];
      if (idVoice) utterance.voice = idVoice;
      utterance.lang = "id-ID";
      utterance.rate = ttsSpeed;
      utterance.pitch = 1;
      utterance.onstart = () => { setTtsActive(true); setTtsPaused(false); };
      utterance.onend = () => { setTtsActive(false); setTtsPaused(false); };
      utterance.onerror = (e) => { console.error("TTS error:", e); setTtsActive(false); setTtsPaused(false); };
      utterance.onpause = () => setTtsPaused(true);
      utterance.onresume = () => setTtsPaused(false);
      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      setTtsActive(true);
      setTtsPaused(false);
    };

    // iOS fix: voices may not be loaded yet
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      startSpeak();
    } else {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.onvoiceschanged = null;
        startSpeak();
      };
      // Trigger voice load on iOS
      window.speechSynthesis.speak(new SpeechSynthesisUtterance(""));
      window.speechSynthesis.cancel();
      setTimeout(startSpeak, 200);
    }
  };

  const handleStopTTS = () => {
    window.speechSynthesis.cancel();
    setTtsActive(false);
    setTtsPaused(false);
  };

  const handleSpeedChange = (speed) => {
    setTtsSpeed(speed);
    // If currently speaking, restart with new speed
    if (ttsActive) {
      const wasPlaying = !ttsPaused;
      window.speechSynthesis.cancel();
      if (wasPlaying) {
        const text = buildLessonText();
        const utterance = new SpeechSynthesisUtterance(text);
        const voices2 = window.speechSynthesis.getVoices();
        const idV = voices2.find(v => v.lang.startsWith("id")) || voices2.find(v => v.lang.startsWith("en")) || voices2[0];
        if (idV) utterance.voice = idV;
        utterance.lang = "id-ID";
        utterance.rate = speed;
        utterance.pitch = 1;
        utterance.onstart = () => { setTtsActive(true); setTtsPaused(false); };
        utterance.onend = () => { setTtsActive(false); setTtsPaused(false); };
        utterance.onerror = () => { setTtsActive(false); setTtsPaused(false); };
        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
        setTtsActive(true);
        setTtsPaused(false);
      } else {
        setTtsActive(false);
        setTtsPaused(false);
      }
    }
  };

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      const res = await axios.post(`${API}/progress/complete/${id}`);
      setIdeas(res.data.ideas);
      setShowIdeas(true);
      setLesson(prev => ({ ...prev, completed: true }));
    } catch (err) { console.error(err); }
    finally { setCompleting(false); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await axios.post(`${API}/notes`, { lesson_id: id, content: newNote, note_type: "note" });
      setNotes(prev => [res.data, ...prev]);
      setNewNote("");
    } catch (err) { console.error(err); }
  };

  const deleteNote = async (noteId) => {
    await axios.delete(`${API}/notes/${noteId}`);
    setNotes(prev => prev.filter(n => n.id !== noteId));
  };

  if (loading) return <PageLoader />;
  if (!lesson) return <div className="p-8 text-[#94A3B8]">Lesson tidak ditemukan.</div>;

  return (
    <div className="relative">
      {/* Reading Progress Bar */}
      <div className="fixed top-12 md:top-0 left-0 md:left-64 right-0 h-0.5 bg-white/5 z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      <div className="flex">
        {/* Main Content */}
        <main className="flex-1 min-w-0 px-4 md:px-12 py-6 md:py-10 max-w-3xl" ref={contentRef}>
          {/* Back Button + Listen Button Row */}
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <button
              onClick={() => { handleStopTTS(); navigate("/knowledge"); }}
              data-testid="back-to-knowledge"
              className="flex items-center gap-2 text-xs text-[#94A3B8] hover:text-white transition-colors duration-200"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span style={{ fontFamily: "'Inter', sans-serif" }}>Kembali ke Knowledge Base</span>
            </button>

            {/* TTS Button */}
            <button
              data-testid="tts-listen-btn"
              onClick={handleTTS}
              className={"flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-xl border transition-all duration-200 " + (
                ttsActive
                  ? "bg-indigo-500/20 border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/30"
                  : "bg-white/5 border-white/15 text-[#94A3B8] hover:text-white hover:border-white/30"
              )}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {ttsActive && !ttsPaused ? (
                <><Pause className="w-3.5 h-3.5" />Jeda</>
              ) : ttsActive && ttsPaused ? (
                <><Play className="w-3.5 h-3.5" />Lanjutkan</>
              ) : (
                <><Volume2 className="w-3.5 h-3.5" />Dengarkan Lesson</>
              )}
            </button>
          </div>

          {/* Lesson Header */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2 py-1 rounded-full">
                Hari {lesson.day} \u00b7 Kategori {lesson.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-[#94A3B8]">
                <Clock className="w-3 h-3" />
                {lesson.duration_minutes} menit
              </span>
              {lesson.completed && (
                <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  Selesai
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-3 md:mb-4 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {lesson.title}
            </h1>
            <p className="text-base text-[#94A3B8] leading-relaxed mb-8 border-l-2 border-indigo-500/30 pl-4" style={{ fontFamily: "'IBM Plex Serif', serif" }}>
              {lesson.intro}
            </p>
          </motion.div>

          {/* Lesson Sections */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="space-y-5"
          >
            {(lesson.sections || []).map((section, i) => (
              <SectionBlock key={i} section={section} />
            ))}
          </motion.div>

          {/* Key Takeaways */}
          {lesson.key_takeaways?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="mt-10 bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-6"
            >
              <h3 className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <BookOpen className="w-4 h-4" />
                Key Takeaways
              </h3>
              <ul className="space-y-2.5">
                {lesson.key_takeaways.map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-indigo-400 text-xs font-mono mt-0.5 flex-shrink-0">{String(i + 1).padStart(2, '0')}.</span>
                    <span className="text-sm text-[#F8FAFC] leading-relaxed" style={{ fontFamily: "'IBM Plex Serif', serif" }}>{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Tags */}
          {lesson.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {lesson.tags.map(tag => (
                <span key={tag} className="text-xs font-mono text-[#94A3B8] bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Complete Button */}
          <div className="mt-10 pt-8 border-t border-white/10">
            {lesson.completed ? (
              <div className="flex items-center gap-3">
                <button
                  data-testid="view-ideas-btn"
                  onClick={() => setShowIdeas(true)}
                  className="flex items-center gap-2 bg-indigo-500/15 border border-indigo-500/25 text-indigo-300 text-sm px-5 py-2.5 rounded-xl hover:bg-indigo-500/20 transition-colors duration-200"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  <Sparkles className="w-4 h-4" />
                  Lihat Ide Konten
                </button>
                <span className="text-sm text-emerald-400 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  Lesson selesai
                </span>
              </div>
            ) : (
              <button
                data-testid="complete-lesson-btn"
                onClick={handleComplete}
                disabled={completing}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-colors duration-200"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {completing ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Menghasilkan ide konten...</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4" />Selesai Baca &amp; Dapatkan Ide Konten</>
                )}
              </button>
            )}
          </div>
          {ttsActive && <div className="h-20" />}
        </main>

        {/* Notes Sidebar Toggle */}
        <div className="w-12 flex-shrink-0 pt-10 flex flex-col items-center">
          <button
            data-testid="toggle-notes-btn"
            onClick={() => setShowNotePanel(!showNotePanel)}
            className={"p-2.5 rounded-xl border transition-colors duration-200 " + (
              showNotePanel
                ? "bg-indigo-500/15 border-indigo-500/25 text-indigo-400"
                : "border-white/10 text-[#94A3B8] hover:text-white hover:border-white/20"
            )}
          >
            <StickyNote className="w-4 h-4" />
          </button>
          {notes.length > 0 && (
            <span className="mt-1.5 text-[10px] font-mono text-indigo-400">{notes.length}</span>
          )}
        </div>

        {/* Notes Panel */}
        <AnimatePresence>
          {showNotePanel && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex-shrink-0 border-l border-white/10 bg-[#0A0A0C] overflow-hidden"
            >
              <div className="p-5 w-[280px]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Catatan</h3>
                  <button onClick={() => setShowNotePanel(false)} className="text-[#94A3B8] hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mb-4">
                  <textarea
                    data-testid="note-input"
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    placeholder="Tulis catatanmu di sini..."
                    rows={3}
                    className="w-full bg-[#121216] border border-white/10 rounded-xl p-3 text-xs text-white placeholder-[#4a5568] focus:outline-none focus:border-indigo-500/40 resize-none"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) addNote(); }}
                  />
                  <button
                    data-testid="add-note-btn"
                    onClick={addNote}
                    className="mt-2 flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 px-3 py-1.5 rounded-lg transition-colors duration-200"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <Plus className="w-3 h-3" />
                    Simpan Catatan
                  </button>
                </div>
                <div className="space-y-2">
                  {notes.map(note => (
                    <div key={note.id} className="bg-[#121216] border border-white/10 rounded-xl p-3 group">
                      <p className="text-xs text-[#F8FAFC] leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{note.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-[#4a5568]">{new Date(note.created_at).toLocaleDateString('id-ID')}</span>
                        <button onClick={() => deleteNote(note.id)} className="text-[#4a5568] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all duration-200">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating TTS Player */}
      <AnimatePresence>
        {ttsActive && (
          <motion.div
            data-testid="tts-player"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 z-50"
            style={{ left: "50%", transform: "translateX(-50%)" }}
          >
            <div className="bg-[#121216] border border-white/15 rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3 w-[calc(100vw-2rem)] md:w-[380px] max-w-lg">
              {/* Animated indicator */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {[0,1,2].map(i => (
                  <motion.div
                    key={i}
                    className="w-0.5 bg-indigo-400 rounded-full"
                    animate={ttsPaused ? { height: 8 } : { height: [8, 20, 8] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
                  />
                ))}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {ttsPaused ? "Dijeda" : "Membaca..."} {lesson.title}
                </p>
                <p className="text-[10px] text-[#94A3B8] mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  Web Speech · {ttsSpeed}x
                </p>
              </div>

              {/* Play/Pause */}
              <button
                data-testid="tts-play-pause-btn"
                onClick={handleTTS}
                className="w-8 h-8 flex items-center justify-center bg-indigo-600 hover:bg-indigo-500 rounded-full transition-colors duration-200 flex-shrink-0"
              >
                {ttsPaused ? <Play className="w-3.5 h-3.5 text-white" /> : <Pause className="w-3.5 h-3.5 text-white" />}
              </button>

              {/* Speed */}
              <select
                data-testid="tts-speed-select"
                value={ttsSpeed}
                onChange={e => handleSpeedChange(parseFloat(e.target.value))}
                className="text-xs text-[#94A3B8] bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 focus:outline-none hover:border-white/20 cursor-pointer"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <option value={0.75}>0.75x</option>
                <option value={1}>1x</option>
                <option value={1.25}>1.25x</option>
                <option value={1.5}>1.5x</option>
                <option value={2}>2x</option>
              </select>

              {/* Stop */}
              <button
                data-testid="tts-stop-btn"
                onClick={handleStopTTS}
                className="text-[#94A3B8] hover:text-red-400 transition-colors flex-shrink-0"
              >
                <Square className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Ideas Modal */}
      <AnimatePresence>
        {showIdeas && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={e => { if (e.target === e.currentTarget) setShowIdeas(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className="bg-[#121216] border border-white/15 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Ide Konten Untukmu</h2>
                  <p className="text-xs text-[#94A3B8] mt-0.5">Berdasarkan lesson yang baru kamu selesaikan</p>
                </div>
                <button onClick={() => setShowIdeas(false)} className="text-[#94A3B8] hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {ideas && ideas.length > 0 ? (
                <div className="space-y-4">
                  {ideas.map((idea, i) => <IdeaCard key={idea.id || i} idea={idea} index={i} />)}
                  <button
                    onClick={() => { setShowIdeas(false); navigate('/vault'); }}
                    className="w-full flex items-center justify-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 border border-indigo-500/20 rounded-xl py-2.5 mt-2 hover:border-indigo-500/40 transition-colors duration-200"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    Lihat di Content Vault <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center py-8 text-[#94A3B8] text-sm">Memuat ide konten...</div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SectionBlock({ section }) {
  const baseText = { fontFamily: "'IBM Plex Serif', serif", lineHeight: "1.8" };
  switch (section.type) {
    case "section_title":
      return <h2 className="text-lg font-bold text-white mt-8 mb-3 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{section.text}</h2>;
    case "quote":
      return (
        <blockquote className="border-l-2 border-indigo-500 pl-5 py-2">
          <p className="text-base text-indigo-200 italic leading-relaxed" style={baseText}>{section.text}</p>
          {section.source && <cite className="text-xs text-[#94A3B8] not-italic mt-2 block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>\u2014 {section.source}</cite>}
        </blockquote>
      );
    case "example":
      return (
        <div className="bg-[#1A1A20] border border-white/10 rounded-xl p-5">
          <span className="text-[10px] font-mono text-purple-400 uppercase tracking-widest">Contoh Nyata</span>
          <p className="text-sm text-[#F8FAFC] leading-relaxed mt-2 whitespace-pre-line" style={baseText}>{section.text}</p>
        </div>
      );
    default:
      return <p className="text-base text-[#F8FAFC] leading-relaxed whitespace-pre-line" style={baseText}>{section.text}</p>;
  }
}

function IdeaCard({ idea, index }) {
  const platforms = idea.platforms || [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.07 }}
      data-testid={`idea-card-${index}`}
      className="bg-[#1A1A20] border border-white/10 rounded-xl p-4"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-white leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{idea.title}</h4>
        <div className="flex gap-1 flex-shrink-0">
          {platforms.map(p => <span key={p} className="text-[10px] text-purple-400 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded-full">{p}</span>)}
        </div>
      </div>
      {idea.angle && <p className="text-xs text-indigo-300 mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{idea.angle}</p>}
      {idea.hook && <p className="text-xs text-[#94A3B8] italic leading-relaxed" style={{ fontFamily: "'IBM Plex Serif', serif" }}>{idea.hook}</p>}
    </motion.div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-[60vh]">
      <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );
}
